import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { Car, Plus, Search, Edit, Trash2, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const defaultForm = { plate: "", model: "", driver: "", device_id: "", client_id: "", notes: "" };

export default function VehiclesPage() {
    const { t } = useLang();
    const { user } = useAuth();
    const [vehicles, setVehicles] = useState([]);
    const [clients, setClients] = useState([]);
    const [devices, setDevices] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const canEdit = ["admin", "staff"].includes(user?.role);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (search) params.q = search;
            const [vRes, cRes, dRes] = await Promise.all([
                axios.get(`${API}/vehicles`, { params, withCredentials: true }),
                axios.get(`${API}/clients`, { params: { limit: 500 }, withCredentials: true }),
                axios.get(`${API}/devices`, { params: { limit: 1000, status: "active" }, withCredentials: true }),
            ]);
            setVehicles(vRes.data.items); setTotal(vRes.data.total);
            setClients(cRes.data.items);
            setDevices(dRes.data.items);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => {
        const timer = setTimeout(fetchAll, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const openAdd = () => { setEditItem(null); setForm(defaultForm); setError(""); setShowModal(true); };
    const openEdit = (item) => { setEditItem(item); setForm({ plate: item.plate, model: item.model, driver: item.driver || "", device_id: item.device_id || "", client_id: item.client_id || "", notes: item.notes || "" }); setError(""); setShowModal(true); };

    const handleSubmit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const payload = { ...form };
            if (!payload.device_id) delete payload.device_id;
            if (!payload.client_id) delete payload.client_id;
            if (editItem) {
                await axios.put(`${API}/vehicles/${editItem.id}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${API}/vehicles`, payload, { withCredentials: true });
            }
            setShowModal(false); fetchAll();
        } catch (e) { setError(e.response?.data?.detail || t("error")); }
        setSaving(false);
    };

    const handleDelete = async (item) => {
        if (!window.confirm(t("confirm_delete"))) return;
        try {
            await axios.delete(`${API}/vehicles/${item.id}`, { withCredentials: true });
            fetchAll();
        } catch (e) { alert(e.response?.data?.detail || t("error")); }
    };

    const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Car size={20} className="text-[#E7212E]" /> {t("vehicles")}
                    </h1>
                    <p className="text-sm text-slate-500 mt-0.5">{total} automjete gjithsej</p>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <button onClick={async () => {
                            try {
                                const res = await axios.get(`${API}/export/vehicles.csv`, { withCredentials: true, responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement("a"); link.href = url;
                                link.setAttribute("download", `automjetet-${new Date().toISOString().substring(0, 10)}.csv`);
                                document.body.appendChild(link); link.click(); link.remove();
                            } catch { alert("Gabim gjatë eksportimit"); }
                        }} data-testid="export-vehicles-btn"
                            className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium px-3 py-2 rounded transition-colors">
                            <Download size={14} /> CSV
                        </button>
                        <button onClick={openAdd} data-testid="add-vehicle-btn"
                            className="flex items-center gap-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                            <Plus size={15} /> {t("add")}
                        </button>
                    </div>
                )}
            </div>

            <div className="relative max-w-sm mb-4">
                <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                <input
                    placeholder="Kërko targë, model, shofer..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    data-testid="vehicle-search"
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]"
                />
            </div>

            <div className="bg-white border border-slate-200 rounded overflow-hidden">
                <table className="w-full crm-table" data-testid="vehicles-table">
                    <thead>
                        <tr>
                            <th>{t("plate")}</th>
                            <th>{t("model")}</th>
                            <th>{t("driver")}</th>
                            <th>IMEI</th>
                            <th>{t("client")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">{t("loading")}</td></tr>
                        ) : vehicles.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">{t("no_data")}</td></tr>
                        ) : vehicles.map(v => (
                            <tr key={v.id} data-testid={`vehicle-row-${v.id}`}>
                                <td>
                                    <span className="font-mono font-semibold text-slate-900 bg-slate-100 px-2 py-0.5 rounded text-sm">{v.plate}</span>
                                </td>
                                <td className="text-sm">{v.model || "—"}</td>
                                <td className="text-sm">{v.driver || "—"}</td>
                                <td>
                                    {v.imei ? <span className="font-mono text-xs text-slate-600">{v.imei}</span> : <span className="text-slate-400 text-xs">—</span>}
                                </td>
                                <td className="text-sm font-medium">{v.client_name || <span className="text-slate-400 text-xs">{t("no_client")}</span>}</td>
                                <td>
                                    <div className="flex items-center gap-1">
                                        {canEdit && (
                                            <button onClick={() => openEdit(v)}
                                                className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" data-testid={`edit-vehicle-${v.id}`}>
                                                <Edit size={14} />
                                            </button>
                                        )}
                                        {user?.role === "admin" && (
                                            <button onClick={() => handleDelete(v)}
                                                className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600" data-testid={`delete-vehicle-${v.id}`}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editItem ? "Modifiko Automjetin" : "Shto Automjet"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Targa *</label>
                                <input required value={form.plate} onChange={e => f("plate", e.target.value.toUpperCase())}
                                    data-testid="vehicle-plate-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Modeli</label>
                                <input value={form.model} onChange={e => f("model", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Shoferi</label>
                                <input value={form.driver} onChange={e => f("driver", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">IMEI / Pajisja</label>
                                <Select value={form.device_id || "none"} onValueChange={v => f("device_id", v === "none" ? "" : v)}>
                                    <SelectTrigger><SelectValue placeholder="Pa pajisje" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— Pa pajisje</SelectItem>
                                        {devices.map(d => <SelectItem key={d.id} value={d.id}>{d.imei} {d.model ? `(${d.model})` : ""}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("client")}</label>
                            <Select value={form.client_id || "none"} onValueChange={v => f("client_id", v === "none" ? "" : v)}>
                                <SelectTrigger><SelectValue placeholder={t("no_client")} /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— {t("no_client")}</SelectItem>
                                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {error && <p className="text-rose-600 text-sm bg-rose-50 p-2 rounded">{error}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowModal(false)}
                                className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
                            <button type="submit" disabled={saving} data-testid="save-vehicle-btn"
                                className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded disabled:opacity-60">
                                {saving ? "Duke ruajtur..." : t("save")}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
